/**
 * IIIF Manifest Processor
 * IIIFマニフェストからの画像取得と処理
 */

import { NDLKotenOCR, ProcessOptions, ProcessResult } from './ndl-koten-ocr';
import { TEIConverter, TEIConversionData } from './tei-converter';

export interface IIIFImageInfo {
  canvasId: string;
  imageUrl: string;
  thumbnailUrl: string;
  label: string;
  index: number;
}

export interface IIIFProcessOptions extends ProcessOptions {
  maxImages?: number;
  downloadImages?: boolean;
  onImageProgress?: (imageIndex: number, progress: number, message: string) => void;
}

export class IIIFProcessor {
  private ocrEngine: NDLKotenOCR;
  private teiConverter: TEIConverter;

  constructor(ocrEngine?: NDLKotenOCR) {
    this.ocrEngine = ocrEngine || new NDLKotenOCR();
    this.teiConverter = new TEIConverter();
  }

  /**
   * IIIFマニフェストから画像リストを取得する静的メソッド
   */
  static async getImagesFromManifest(manifestUrl: string): Promise<IIIFImageInfo[]> {
    const processor = new IIIFProcessor();
    const manifest = await processor.fetchManifest(manifestUrl);
    return processor.extractImages(manifest);
  }

  /**
   * IIIFマニフェストURLから画像を取得してOCR処理を実行
   */
  async processManifestUrl(
    manifestUrl: string,
    options: IIIFProcessOptions = {}
  ): Promise<{
    results: ProcessResult[];
    teiXml: string;
    manifest: any;
  }> {
    // マニフェストを取得
    const manifest = await this.fetchManifest(manifestUrl);

    // 画像情報を抽出
    const imageInfos = this.extractImages(manifest);

    // 処理する画像数を制限
    const imagesToProcess = options.maxImages
      ? imageInfos.slice(0, options.maxImages)
      : imageInfos;

    // 各画像を処理
    const results: ProcessResult[] = [];

    for (let i = 0; i < imagesToProcess.length; i++) {
      const imageInfo = imagesToProcess[i];

      if (options.onImageProgress) {
        options.onImageProgress(i, 0, `画像 ${i + 1}/${imagesToProcess.length} を処理中...`);
      }

      // 画像を取得
      const imageData = await this.fetchImage(imageInfo.imageUrl);

      // OCR処理
      const result = await this.ocrEngine.process(imageData, {
        imageName: imageInfo.label,
        onProgress: (progress, message) => {
          if (options.onImageProgress) {
            options.onImageProgress(i, progress, message);
          }
        }
      });

      // 結果に画像情報を追加
      const enhancedResult = {
        ...result,
        imageUrl: imageInfo.imageUrl,
        imageName: imageInfo.label,
        imageWidth: imageData.width,
        imageHeight: imageData.height
      };

      results.push(enhancedResult);

      if (options.onImageProgress) {
        options.onImageProgress(i, 100, `画像 ${i + 1}/${imagesToProcess.length} 完了`);
      }
    }

    // TEI/XMLに変換
    const teiData: TEIConversionData = {
      title: this.getManifestTitle(manifest),
      sourceUrl: manifestUrl,
      results: results
    };

    const teiXml = this.teiConverter.convertOCRResults(teiData);

    return {
      results,
      teiXml,
      manifest
    };
  }

  /**
   * マニフェストを取得
   */
  public async fetchManifest(url: string): Promise<any> {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch manifest: HTTP ${response.status}`);
    }

    const manifest = await response.json();

    // Presentation API v2の場合はv3形式に変換が必要
    // ここでは簡易的な変換を実装
    if (manifest['@context']?.includes('presentation/2')) {
      return this.convertV2ToV3(manifest);
    }

    return manifest;
  }

  /**
   * マニフェストから画像情報を抽出
   */
  public extractImages(manifest: any): IIIFImageInfo[] {
    const imageInfos: IIIFImageInfo[] = [];

    if (!manifest.items) {
      return imageInfos;
    }

    manifest.items.forEach((canvas: any, canvasIndex: number) => {
      // サムネイルURLの取得
      let thumbnailUrl: string | undefined;
      if (canvas.thumbnail) {
        if (Array.isArray(canvas.thumbnail) && canvas.thumbnail[0]) {
          thumbnailUrl = canvas.thumbnail[0].id || canvas.thumbnail[0];
        } else if (typeof canvas.thumbnail === 'string') {
          thumbnailUrl = canvas.thumbnail;
        } else if (canvas.thumbnail.id) {
          thumbnailUrl = canvas.thumbnail.id;
        }
      }

      if (canvas.items?.[0]?.items) {
        canvas.items[0].items.forEach((annotation: any) => {
          if (annotation.body?.id) {
            const imageUrl = annotation.body.id;
            const label = this.getCanvasLabel(canvas) || `Page ${canvasIndex + 1}`;

            // サムネイルが無い場合は、画像URLを小さいサイズで要求
            if (!thumbnailUrl && imageUrl.includes('/full/full/')) {
              thumbnailUrl = imageUrl.replace('/full/full/', '/full/200,/');
            } else if (!thumbnailUrl) {
              thumbnailUrl = imageUrl;
            }

            imageInfos.push({
              canvasId: canvas.id,
              imageUrl: imageUrl,
              thumbnailUrl: thumbnailUrl || imageUrl,
              label: label,
              index: canvasIndex
            });
          }
        });
      }
    });

    return imageInfos;
  }

  /**
   * 画像を取得してImageDataに変換
   */
  private async fetchImage(imageUrl: string): Promise<ImageData> {
    // ブラウザ環境の場合
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to create canvas context');
      }

      ctx.drawImage(img, 0, 0);
      return ctx.getImageData(0, 0, canvas.width, canvas.height);
    }

    // Node.js環境の場合は別の実装が必要
    throw new Error('Image fetching in Node.js environment is not yet implemented');
  }

  /**
   * Presentation API v2からv3への簡易変換
   */
  private convertV2ToV3(v2Manifest: any): any {
    // 基本的な構造の変換
    const v3Manifest: any = {
      '@context': 'http://iiif.io/api/presentation/3/context.json',
      id: v2Manifest['@id'],
      type: 'Manifest',
      label: this.convertLabel(v2Manifest.label),
      items: []
    };

    // sequences[0].canvasesをitemsに変換
    if (v2Manifest.sequences?.[0]?.canvases) {
      v3Manifest.items = v2Manifest.sequences[0].canvases.map((canvas: any) => ({
        id: canvas['@id'],
        type: 'Canvas',
        label: this.convertLabel(canvas.label),
        width: canvas.width,
        height: canvas.height,
        items: [{
          id: `${canvas['@id']}/page`,
          type: 'AnnotationPage',
          items: canvas.images?.map((image: any) => ({
            id: `${canvas['@id']}/annotation`,
            type: 'Annotation',
            motivation: 'painting',
            body: {
              id: image.resource?.['@id'] || image.resource?.id,
              type: 'Image',
              format: image.resource?.format || 'image/jpeg',
              width: image.resource?.width,
              height: image.resource?.height
            },
            target: canvas['@id']
          })) || []
        }],
        thumbnail: canvas.thumbnail ? [{
          id: canvas.thumbnail['@id'] || canvas.thumbnail,
          type: 'Image'
        }] : undefined
      }));
    }

    return v3Manifest;
  }

  /**
   * ラベルの変換（文字列または多言語オブジェクト）
   */
  private convertLabel(label: any): any {
    if (typeof label === 'string') {
      return { none: [label] };
    }
    if (Array.isArray(label)) {
      return { none: label };
    }
    return label;
  }

  /**
   * マニフェストタイトルの取得
   */
  private getManifestTitle(manifest: any): string {
    const label = manifest.label;
    if (typeof label === 'string') {
      return label;
    }
    if (label?.ja?.[0]) {
      return label.ja[0];
    }
    if (label?.en?.[0]) {
      return label.en[0];
    }
    if (label?.none?.[0]) {
      return label.none[0];
    }
    return 'IIIF Manifest';
  }

  /**
   * キャンバスラベルの取得
   */
  private getCanvasLabel(canvas: any): string {
    const label = canvas.label;
    if (typeof label === 'string') {
      return label;
    }
    if (label?.ja?.[0]) {
      return label.ja[0];
    }
    if (label?.en?.[0]) {
      return label.en[0];
    }
    if (label?.none?.[0]) {
      return label.none[0];
    }
    return '';
  }
}